import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Send, ArrowLeft, Store, ChefHat } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(params.get("id") || null);
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const msgBox = useRef();

  const loadList = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data.conversations);
      if (!selected && data.conversations.length) setSelected(data.conversations[0].conversation_id);
    } finally { setLoading(false); }
  };

  const loadMessages = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setMessages(data.messages);
      setOther(data.other);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    if (!selected) return;
    setParams(selected ? { id: selected } : {});
    loadMessages(selected);
    const iv = setInterval(() => loadMessages(selected), 5000);
    return () => clearInterval(iv);
  }, [selected]);
  useEffect(() => { msgBox.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || !selected) return;
    const content = input;
    setInput("");
    const optimistic = {
      message_id: `local_${Date.now()}`,
      conversation_id: selected,
      sender_id: user?.user_id,
      sender_name: user?.name,
      content,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages(prev => [...prev, optimistic]);
    try {
      await api.post(`/conversations/${selected}/messages`, { content });
      loadMessages(selected); loadList();
    } catch (err) {
      setMessages(prev => prev.filter(m => m.message_id !== optimistic.message_id));
      setInput(content);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="anim-fade-up">
      <div className="mb-6">
        <h1 className="text-3xl font-display font-extrabold">Mensagens</h1>
        <p className="text-zinc-400 text-sm mt-1">Conversas diretas entre comércios e profissionais.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4" style={{ minHeight: "560px" }}>
        {/* Sidebar */}
        <div className={`cw-card p-0 overflow-hidden ${selected ? "hidden md:block" : ""}`}>
          <div className="p-4 border-b border-zinc-800 font-semibold text-sm flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-red-500" /> Conversas
          </div>
          {loading ? (
            <div className="p-6 text-center text-zinc-500 text-sm">Carregando…</div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">
              Nenhuma conversa ainda. Você pode iniciar uma pela tela de uma diária, clicando no perfil da outra parte.
            </div>
          ) : conversations.map(c => {
            const otherP = c.other || {};
            const active = selected === c.conversation_id;
            const Icon = otherP.role === "comercio" ? Store : ChefHat;
            return (
              <button key={c.conversation_id} onClick={() => setSelected(c.conversation_id)}
                      data-testid={`conv-${c.conversation_id}`}
                      className={`w-full text-left p-4 border-b border-zinc-800/60 hover:bg-zinc-800/40 transition-colors ${active ? "bg-red-500/10" : ""}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{otherP.business_name || otherP.name || "—"}</div>
                    <div className="text-xs text-zinc-400 truncate">{c.last_message || "Sem mensagens ainda"}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Chat panel */}
        <div className={`md:col-span-2 cw-card p-0 flex flex-col ${!selected ? "hidden md:flex" : "flex"}`} style={{ height: "560px" }}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">
              Selecione uma conversa
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
                <button className="md:hidden" onClick={() => setSelected(null)} data-testid="btn-back-conv">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                  {other?.role === "comercio" ? <Store className="w-5 h-5 text-red-500" /> : <ChefHat className="w-5 h-5 text-amber-500" />}
                </div>
                <div>
                  <div className="font-semibold">{other?.business_name || other?.name || "—"}</div>
                  <div className="text-xs text-zinc-500">{other?.role === "comercio" ? "Comércio" : `${other?.specialty || "Profissional"} disponível`}</div>
                </div>
              </div>
              <div ref={msgBox} className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.length === 0 ? (
                  <div className="text-center text-zinc-500 text-sm mt-8">Comece a conversa 👋</div>
                ) : messages.map(m => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-red-600 text-white" : "bg-zinc-800 text-zinc-100"} ${m._pending ? "opacity-60" : ""}`}>
                        {m.content}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 border-t border-zinc-800 flex gap-2">
                <input value={input} onChange={e => setInput(e.target.value)}
                       onKeyDown={e => e.key === "Enter" && send()}
                       placeholder="Digite sua mensagem…" className="cw-input" data-testid="input-dm" />
                <button onClick={send} className="cw-btn-primary !px-3" data-testid="btn-dm-send"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
